import type { AIModel } from '../../api/structs/model';
import { CHAT_PROVIDERS } from '../../api/structs/providers';

// The save's agent model check, mirrored so the picker offers only models it
// would accept (Go pipeline.CheckAgentModel → agentruntime.ResolveCapableModel).

/** Go types.AgentEngineTools with ReturnsImages: their results show the model
 *  images, so an agent that may call one needs vision (validation.AgentRequirements).
 *  Sourcing tools carry no such flag. */
export const AGENT_TOOLS_RETURNING_IMAGES = ['media.view', 'image.generate'];

/** Go agentAllowedProviders (internal/pipeline/agent_host.go): the providers
 *  backend tool chat can run (pkg/aiChat/toolchat.go). Vertex AI joins with
 *  VX-1; its models are still offered only once the registry gives them the
 *  "tools" feature (missingAgentCapabilities). Anthropic's own API is offered
 *  only on a server that has its key: /models lists its models then. */
export const AGENT_PROVIDERS = ['openrouter', 'modal_endpoints', 'vertexai', 'anthropic'];

export interface AgentModelRequirements {
  tools: boolean;
  vision: boolean;
}

/** What an agent with these tools needs from its model: tool calling always,
 *  vision when one of its tools shows it images. */
export const agentModelRequirements = (tools: string[]): AgentModelRequirements => ({
  tools: true,
  vision: tools.some((tool) => AGENT_TOOLS_RETURNING_IMAGES.includes(tool.trim())),
});

const declares = (values: string[] | undefined, want: string): boolean =>
  (values ?? []).some((value) => value.trim().toLowerCase() === want);

/** Go modelregistry.MissingCapabilities, in its order: vision comes from
 *  capabilities.input_modalities ("image"), tools from capabilities.features. */
export const missingAgentCapabilities = (model: AIModel, req: AgentModelRequirements): string[] => {
  const missing: string[] = [];
  if (req.vision && !declares(model.capabilities?.input_modalities, 'image')) missing.push('vision');
  if (req.tools && !declares(model.capabilities?.features, 'tools')) missing.push('tools');
  return missing;
};

/** A catalog row that names no provider is OpenRouter's (ResolveCapableModel). */
const providerOf = (model: AIModel): string => (model.provider || 'openrouter').trim().toLowerCase();

const canRun = (model: AIModel, req: AgentModelRequirements): boolean =>
  missingAgentCapabilities(model, req).length === 0 && AGENT_PROVIDERS.includes(providerOf(model));

const NEEDS: Record<string, string> = { vision: 'image input (vision)', tools: 'tool calling' };

/**
 * '' when `model` can run an agent with these tools; otherwise the warning to
 * show for it, pointing at the same model on OpenRouter when the catalog has a
 * version that can (e.g. Vertex AI "gemini-3.6-flash" → "google/gemini-3.6-flash").
 */
export const agentModelIneligibility = (model: AIModel, catalog: AIModel[], tools: string[]): string => {
  const req = agentModelRequirements(tools);
  if (canRun(model, req)) return '';
  const missing = missingAgentCapabilities(model, req);
  const providerLabel = CHAT_PROVIDERS.find((p) => p.value === providerOf(model))?.label ?? providerOf(model);
  const reason = missing.length > 0
    ? `needs ${missing.map((item) => NEEDS[item]).join(' and ')}`
    : `${providerLabel} can't run agents`;
  const openRouterVersion = catalog.find(
    (candidate) =>
      providerOf(candidate) === 'openrouter' && candidate.id.endsWith(`/${model.id}`) && canRun(candidate, req)
  );
  const suggestion = openRouterVersion
    ? `Use the OpenRouter version, e.g. ${openRouterVersion.id}.`
    : `Choose an OpenRouter model with tool calling${req.vision ? ' and vision' : ''}.`;
  return `This model can't run an agent: ${reason}. ${suggestion}`;
};
